import { FC, memo } from 'react';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';

import { useNotificationContext } from 'application/providers/NotificationProvider';
import { BILLING_PAGE_PATH_ALIAS } from 'application/consts';
import { useAccountCompanyId } from 'application/hooks';

import {
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  useAuthUserGetSberReauthUriQuery,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import IconRenderer from '@zvuk-b2b/react-uikit/ui/icons/IconRenderer';

import { TEXTS } from './texts';
import icon from './sber_id_logo_small.svg';

import styles from './index.module.scss';

const REDIRECT_URL = `${window.location.hostname}${BILLING_PAGE_PATH_ALIAS}`;

const SberIDReauth: FC = () => {
  const notification = useNotificationContext();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const sberIdButtonDisabled = Boolean(userData?.currentUserPureQuery?.mainCompany?.sberIdButtonState?.isDisabled);
  const companyId = useAccountCompanyId();

  const { data } = useAuthUserGetSberReauthUriQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    variables: { companyId, redirectUri: REDIRECT_URL },
  });

  const handleReauth = () => {
    const link = data?.userGetSberLinkUri;

    if (link) {
      window.location.href = link;

      notification.showInfoNotification({
        children: TEXTS.SBER_ID_REAUTH_START_TEXT,
      });
    } else {
      notification.showErrorNotification({
        children: TEXTS.SBER_ID_REAUTH_ERROR_TEXT,
      });
    }
  };

  return (
    <div className={styles.container}>
      <Button
        elementId="button_sber_business_online_reauth"
        className={styles.sberIdButton}
        type={ButtonType.PLAIN}
        isLoading={false}
        isDisabled={sberIdButtonDisabled}
        onClick={handleReauth}>
        <IconRenderer
          iconHtml={icon}
          className={classNames(styles.icon, {
            [styles.iconDisabled]: sberIdButtonDisabled,
          })}
        />
        {sberIdButtonDisabled ? TEXTS.SBER_ID_DISABLED_TEXT : TEXTS.SBER_ID_TEXT}
      </Button>
      <div className={styles.hint}>{TEXTS.SBER_ID_HINT}</div>
    </div>
  );
};

export default memo(SberIDReauth);
